/**
 * /tools/masterpiece-day?clientId=X — STP-034 · Masterpiece Day (Part 2 of
 * "Workday Audit & Masterpiece Day").
 *
 * The client's ideal-day timeline. Builds on Part 1 (Workday Audit): the form
 * is **locked until the audit is completed**, then **pre-fills its blocks from
 * the audited timeline** with the audit's draining activities **flagged**, so
 * the client redesigns their real day into a better-structured one. Persists
 * via the generic tools route → versioned `client_documents`
 * doc_type=`masterpiece_day`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';
import { AxisIcon } from '@/components/axis-icon';

const AUDIT_AXES = ['energy', 'work', 'love'] as const;
type AuditAxis = (typeof AUDIT_AXES)[number];

/** An activity from the Workday Audit (Part 1). */
interface AuditActivity {
  startTime: string;
  endTime?: string;
  label: string;
  energy: number;
  work: number;
  love: number;
}

/** Energy at or below this in the audit marks an activity as "draining". */
const DRAIN_THRESHOLD = 4;

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** "HH:MM" + minutes → "HH:MM", clamped to 23:59. */
function addMinutes(hhmm: string, mins: number): string {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const total = Math.min(23 * 60 + 59, Number(m[1]) * 60 + Number(m[2]) + mins);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

interface TimeBlock {
  startTime: string;
  endTime: string;
  category: string;
  description: string;
}

interface FormState {
  blocks: TimeBlock[];
  surprised: string;
  oneChange: string;
}

const STARTER_BLOCKS: TimeBlock[] = [
  { startTime: '05:30', endTime: '06:00', category: '', description: '' },
  { startTime: '06:15', endTime: '08:30', category: '', description: '' },
  { startTime: '17:00', endTime: '17:10', category: '', description: '' },
];

const DEFAULT_FORM: FormState = {
  blocks: STARTER_BLOCKS.map((b) => ({ ...b })),
  surprised: '',
  oneChange: '',
};

function parseAuditActivities(payload: unknown): AuditActivity[] {
  if (!payload || typeof payload !== 'object') return [];
  const raw = (payload as Record<string, unknown>).activities;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): AuditActivity => {
      const a = entry as Record<string, unknown>;
      const startTime = typeof a.startTime === 'string' ? a.startTime : '';
      const label = typeof a.label === 'string' ? a.label : '';
      const energy = typeof a.energy === 'number' ? a.energy : 5;
      const work = typeof a.work === 'number' ? a.work : 5;
      const love = typeof a.love === 'number' ? a.love : 5;
      return typeof a.endTime === 'string'
        ? { startTime, endTime: a.endTime, label, energy, work, love }
        : { startTime, label, energy, work, love };
    })
    .filter((a) => a.label.trim().length > 0 && a.startTime.length > 0);
}

export function MasterpieceDayPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  // Workday Audit rework — Part 1 (Workday Audit) data: its timeline pre-fills
  // the blocks, its drains drive the reference + per-block flags, and Part 2
  // stays locked until the audit is completed.
  const [auditActivities, setAuditActivities] = useState<AuditActivity[]>([]);
  const [auditDone, setAuditDone] = useState(false);
  // STP-034 add-block (Workday Audit mechanics) — focus the new block's End
  // field so the client can adjust the auto-filled +30-min duration.
  const endRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusEndIdx, setFocusEndIdx] = useState<number | null>(null);
  useEffect(() => {
    if (focusEndIdx === null) return;
    endRefs.current[focusEndIdx]?.focus();
    setFocusEndIdx(null);
  }, [focusEndIdx]);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const [existing, audit] = await Promise.all([
          api.getToolCompletion(accessToken, clientId, 'masterpiece-day'),
          api.getToolCompletion(accessToken, clientId, 'energy-audit').catch(() => null),
        ]);
        if (!active) return;
        const activities = parseAuditActivities(audit?.payload);
        setAuditActivities(activities);
        setAuditDone(audit?.status === 'completed');

        if (existing?.payload && typeof existing.payload === 'object') {
          // Prior masterpiece submission → hydrate from it.
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          if (Array.isArray(p.blocks) && p.blocks.length > 0) {
            next.blocks = p.blocks.map((raw) => {
              const b = raw as Record<string, unknown>;
              return {
                startTime: typeof b.startTime === 'string' ? b.startTime : '',
                endTime: typeof b.endTime === 'string' ? b.endTime : '',
                category: typeof b.category === 'string' ? b.category : '',
                description: typeof b.description === 'string' ? b.description : '',
              };
            });
          }
          if (p.reflection && typeof p.reflection === 'object') {
            const r = p.reflection as Record<string, unknown>;
            if (typeof r.surprised === 'string') next.surprised = r.surprised;
            if (typeof r.oneChange === 'string') next.oneChange = r.oneChange;
          }
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        } else if (activities.length > 0) {
          // No prior masterpiece → pre-fill the blocks from the audited
          // timeline (carry the real day's structure forward to redesign).
          setForm((prev) => ({
            ...prev,
            blocks: activities.map((a) => ({
              startTime: a.startTime,
              endTime: a.endTime ?? '',
              category: a.label,
              description: '',
            })),
          }));
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  // Activities the audit scored as draining — used to flag blocks to redesign.
  const drainingSet = useMemo(
    () =>
      new Set(
        auditActivities
          .filter((a) => a.energy <= DRAIN_THRESHOLD)
          .map((a) => a.label.trim().toLowerCase()),
      ),
    [auditActivities],
  );

  const blocksValid = form.blocks.every(
    (b) =>
      HHMM.test(b.startTime) &&
      HHMM.test(b.endTime) &&
      b.category.trim().length > 0 &&
      b.description.trim().length > 0,
  );
  const canSubmit = !submitting && hydrated && blocksValid && form.blocks.length > 0;
  // Part 2 unlocks once the Workday Audit is completed (or the client already
  // saved a masterpiece day, so a later audit reset doesn't lock them out).
  const unlocked = auditDone || savedAt !== null;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.masterpieceDay.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const reflection: Record<string, string> = {};
    if (form.surprised.trim()) reflection.surprised = form.surprised.trim();
    if (form.oneChange.trim()) reflection.oneChange = form.oneChange.trim();
    const payload = {
      blocks: form.blocks.map((b) => ({
        startTime: b.startTime,
        endTime: b.endTime,
        category: b.category.trim(),
        description: b.description.trim(),
      })),
      ...(Object.keys(reflection).length > 0 ? { reflection } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'masterpiece-day',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.masterpieceDay.errors.generic'));
      } else {
        setSubmitError(t('tools.masterpieceDay.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="masterpiece-day-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.masterpieceDay.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">{t('tools.masterpieceDay.errors.missingClient')}</p>
      </section>
    );
  }

  const addBlock = (): void => {
    setForm((prev) => {
      if (prev.blocks.length >= 40) return prev;
      // Same continuous-timeline mechanic as the Workday Audit: new start =
      // the previous block's end (fallback its start), new end = +30 min.
      const last = prev.blocks[prev.blocks.length - 1];
      const start =
        [last?.endTime, last?.startTime].find((c): c is string => !!c && HHMM.test(c)) ?? '06:00';
      return {
        ...prev,
        blocks: [
          ...prev.blocks,
          { startTime: start, endTime: addMinutes(start, 30), category: '', description: '' },
        ],
      };
    });
    setFocusEndIdx(form.blocks.length < 40 ? form.blocks.length : null);
  };
  const removeBlock = (idx: number): void => {
    setForm((prev) =>
      prev.blocks.length <= 1
        ? prev
        : { ...prev, blocks: prev.blocks.filter((_, i) => i !== idx) },
    );
  };
  const setBlock = (idx: number, key: keyof TimeBlock, value: string): void => {
    setForm((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b, i) => (i === idx ? { ...b, [key]: value } : b)),
    }));
  };

  return (
    <section className="max-w-4xl mx-auto px-s5 py-s7">
      <div className="flex items-center justify-between mb-s2">
        <Link
          to={`/tools/masterpiece-blueprint?clientId=${encodeURIComponent(clientId)}`}
          className="text-sm text-flame-deep hover:text-flame"
          data-testid="masterpiece-day-back"
        >
          {t('tools.masterpieceDay.backToTool')}
        </Link>
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.masterpieceDay.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.masterpieceDay.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.masterpieceDay.description')}
        </p>
      </header>

      {!unlocked ? (
        <article
          className="rounded-lg border border-flame/40 bg-flame/[0.04] p-s5"
          data-testid="masterpiece-day-locked"
        >
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.masterpieceDay.locked.title')}
          </h2>
          <p className="mt-s2 text-sm text-gray-2">{t('tools.masterpieceDay.locked.body')}</p>
          <Link
            to={`/tools/energy-audit?clientId=${encodeURIComponent(clientId)}`}
            className="btn-primary mt-s3 inline-flex"
            data-testid="masterpiece-day-locked-cta"
          >
            {t('tools.masterpieceDay.locked.cta')}
          </Link>
        </article>
      ) : (
        <form
          onSubmit={(event) => {
            void onSubmit(event);
          }}
          className="space-y-s5"
          noValidate
        >
          {/* Build on Part 1's audit. */}
          <AuditReference activities={auditActivities} />

          <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
            <h2 className="font-display text-display-sm text-ink">
              {t('tools.masterpieceDay.blocks.title')}
            </h2>
            <p className="mt-s2 text-gray-2 italic">{t('tools.masterpieceDay.blocks.hint')}</p>
            {/* Same timeline-spine layout + entry mechanics as the Workday
                Audit (Part 1), minus the KPI sliders, plus the per-block
                "What happens in this block" field. */}
            <ol className="mt-s4 space-y-s2 border-l-2 border-bone-3 pl-s4">
              {form.blocks.map((block, idx) => {
                const flagged =
                  block.category.trim().length > 0 &&
                  drainingSet.has(block.category.trim().toLowerCase());
                return (
                  <li
                    key={idx}
                    className="rounded-md bg-bone px-s3 py-s2 space-y-s2"
                    data-testid={`masterpiece-day-block-${idx}`}
                  >
                    {flagged && (
                      <p
                        className="flex items-center gap-s1 text-xs text-semantic-warning"
                        data-testid={`masterpiece-day-flag-${idx}`}
                      >
                        ⚠ {t('tools.masterpieceDay.flagged')}
                      </p>
                    )}
                    <div className="flex items-center gap-s2">
                      <input
                        type="time"
                        value={block.startTime}
                        onChange={(e) => setBlock(idx, 'startTime', e.target.value)}
                        className="input-field w-[7.5rem] font-mono text-sm"
                        aria-label={t('tools.masterpieceDay.fields.startTime')}
                        data-testid={`masterpiece-day-start-${idx}`}
                      />
                      <span className="text-gray-1">–</span>
                      <input
                        ref={(el) => {
                          endRefs.current[idx] = el;
                        }}
                        type="time"
                        value={block.endTime}
                        onChange={(e) => setBlock(idx, 'endTime', e.target.value)}
                        className="input-field w-[7.5rem] font-mono text-sm"
                        aria-label={t('tools.masterpieceDay.fields.endTime')}
                        data-testid={`masterpiece-day-end-${idx}`}
                      />
                      <input
                        type="text"
                        value={block.category}
                        onChange={(e) => setBlock(idx, 'category', e.target.value)}
                        placeholder={t('tools.masterpieceDay.placeholders.category')}
                        className="input-field flex-1"
                        data-testid={`masterpiece-day-category-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeBlock(idx)}
                        disabled={form.blocks.length <= 1}
                        className="btn-ghost text-sm"
                        aria-label={t('tools.masterpieceDay.blocks.remove')}
                        data-testid={`masterpiece-day-remove-${idx}`}
                      >
                        ×
                      </button>
                    </div>
                    <label className="block">
                      <span className="font-mono text-xs uppercase tracking-wide text-gray-1">
                        {t('tools.masterpieceDay.blocks.whatHappens')}
                      </span>
                      <input
                        type="text"
                        value={block.description}
                        onChange={(e) => setBlock(idx, 'description', e.target.value)}
                        placeholder={t('tools.masterpieceDay.placeholders.description')}
                        className="input-field mt-s1 w-full"
                        data-testid={`masterpiece-day-description-${idx}`}
                      />
                    </label>
                  </li>
                );
              })}
            </ol>
            <button
              type="button"
              onClick={addBlock}
              disabled={form.blocks.length >= 40}
              className="btn-ghost mt-s3"
              data-testid="masterpiece-day-add-block"
            >
              + {t('tools.masterpieceDay.blocks.add')}
            </button>
          </article>

          <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
            <h2 className="font-display text-display-sm text-ink">
              {t('tools.masterpieceDay.reflection.title')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.masterpieceDay.optional')}
              </span>
            </h2>
            <div className="mt-s3 space-y-s3">
              <label className="block">
                <span className="form-label">{t('tools.masterpieceDay.reflection.surprised')}</span>
                <textarea
                  value={form.surprised}
                  onChange={(e) => setForm((prev) => ({ ...prev, surprised: e.target.value }))}
                  className="input-field mt-s2 min-h-20"
                  data-testid="masterpiece-day-surprised"
                  rows={3}
                />
              </label>
              <label className="block">
                <span className="form-label">{t('tools.masterpieceDay.reflection.oneChange')}</span>
                <textarea
                  value={form.oneChange}
                  onChange={(e) => setForm((prev) => ({ ...prev, oneChange: e.target.value }))}
                  className="input-field mt-s2 min-h-20"
                  data-testid="masterpiece-day-one-change"
                  rows={3}
                />
              </label>
            </div>
          </article>

          {submitError && (
            <p role="alert" className="form-error" data-testid="masterpiece-day-submit-error">
              {submitError}
            </p>
          )}

          {savedAt && (
            <p
              role="status"
              className="text-sm text-flame-deep"
              data-testid="masterpiece-day-saved-at"
            >
              ✓ {t('tools.masterpieceDay.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
            </p>
          )}

          <FinalizeWarning />

          <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
            {submitting ? t('tools.masterpieceDay.submitting') : t('tools.masterpieceDay.submit')}
          </button>
        </form>
      )}
    </section>
  );
}

/**
 * Surfaces the Workday Audit (Part 1) so the client designs around it: the
 * day's E→W→L balance, the peak-energy activity, and the draining activities
 * to redesign.
 */
function AuditReference({ activities }: { activities: AuditActivity[] }): JSX.Element | null {
  const { t } = useTranslation();
  if (activities.length === 0) return null;
  const avg = (k: AuditAxis): number =>
    Math.round((activities.reduce((s, a) => s + a[k], 0) / activities.length) * 10) / 10;
  const byEnergy = [...activities].sort((a, b) => b.energy - a.energy);
  const peak = byEnergy[0]!;
  const drains = activities.filter((a) => a.energy <= DRAIN_THRESHOLD);
  return (
    <article
      className="rounded-lg border border-flame/40 bg-flame/[0.04] p-s5"
      data-testid="masterpiece-day-audit-reference"
    >
      <h2 className="font-display text-display-sm text-ink">
        {t('tools.masterpieceDay.audit.title')}
      </h2>
      <p className="mt-s2 text-sm text-gray-2">{t('tools.masterpieceDay.audit.hint')}</p>
      <div className="mt-s3 flex flex-wrap items-center gap-s4">
        <span className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.masterpieceDay.audit.balance')}
        </span>
        {AUDIT_AXES.map((axis) => (
          <span key={axis} className="flex items-center gap-s1" data-testid={`masterpiece-day-audit-${axis}`}>
            <AxisIcon axis={axis} size={15} ariaLabel={t(`common.axis.${axis}`)} />
            <span className="font-display text-base text-ink">{avg(axis)}</span>
          </span>
        ))}
      </div>
      <p className="mt-s2 text-sm text-gray-2">
        <span className="text-semantic-success">▲</span>{' '}
        {t('tools.masterpieceDay.audit.peak', { label: peak.label })}
      </p>
      {drains.length > 0 && (
        <div className="mt-s2" data-testid="masterpiece-day-audit-drains">
          <p className="text-sm text-semantic-danger">{t('tools.masterpieceDay.audit.drains')}</p>
          <ul className="mt-s1 flex flex-wrap gap-s2">
            {drains.map((d, i) => (
              <li
                key={i}
                className="rounded-full bg-semantic-danger/10 px-s2 py-0.5 text-xs text-semantic-danger"
              >
                {d.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}
